class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      const cartDrawerSectionUrl = `${routes.cart_url}?section_id=cart-drawer&ts=${Date.now()}`;
      return fetch(cartDrawerSectionUrl, {
        credentials: 'same-origin',
        cache: 'no-store',
      })
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.sm-w', '.drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          // console.error(e);
        });
    } else {
      const mainCartItemsSectionUrl = `${routes.cart_url}?section_id=main-cart-items&ts=${Date.now()}`;
      return fetch(mainCartItemsSectionUrl, {
        credentials: 'same-origin',
        cache: 'no-store',
      })
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          // console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!window.__cartRecoAddBound) {
  window.__cartRecoAddBound = true;
  document.addEventListener('click', async (event) => {
    const addButton = event.target.closest('.js-cart-reco-add');
    if (!addButton) return;

    event.preventDefault();
    const variantId = Number(addButton.dataset.variantId);
    if (!variantId) return;

    const cartUI = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const sections = cartUI && typeof cartUI.getSectionsToRender === 'function'
      ? cartUI.getSectionsToRender().map((section) => section.id)
      : ['cart-icon-bubble'];

    addButton.disabled = true;
    addButton.setAttribute('aria-disabled', 'true');
    const originalLabel = addButton.textContent;
    addButton.textContent = '...';

    try {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', '1');
      sections.forEach((sectionId) => formData.append('sections', sectionId));
      formData.append('sections_url', window.location.pathname);

      const response = await fetch(`${routes.cart_add_url}`, { ...config, body: formData });
      const parsedResponse = await response.json();

      if (parsedResponse.status) {
        addButton.disabled = false;
        addButton.removeAttribute('aria-disabled');
        addButton.textContent = originalLabel;
        return;
      }

      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (
        cartDrawerItems &&
        parsedResponse.sections &&
        typeof cartDrawerItems.getSectionsToRender === 'function' &&
        typeof cartDrawerItems.getSectionInnerHTML === 'function'
      ) {
        cartDrawerItems.getSectionsToRender().forEach((section) => {
          const targetContainer = document.getElementById(section.id);
          if (!targetContainer) return;

          const elementToReplace = targetContainer.querySelector(section.selector) || targetContainer;
          const sectionHtml = parsedResponse.sections[section.section];
          if (!sectionHtml) return;

          elementToReplace.innerHTML = cartDrawerItems.getSectionInnerHTML(sectionHtml, section.selector);
        });
      } else if (cartUI && typeof cartUI.renderContents === 'function') {
        cartUI.renderContents(parsedResponse);
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-recommendations',
        productVariantId: variantId,
        cartData: parsedResponse,
      });
      setTimeout(renderCartMergedRecommendations, 0);
    } catch (error) {
      addButton.disabled = false;
      addButton.removeAttribute('aria-disabled');
      addButton.textContent = originalLabel;
    }
  });
}

if (!window.__cartDiscountBound) {
  window.__cartDiscountBound = true;

  const hasCartDiscountEvidence = (cartData) => {
    if (!cartData) return false;
    const cartDiscounts = Array.isArray(cartData.cart_level_discount_applications)
      ? cartData.cart_level_discount_applications
      : [];
    if (cartDiscounts.length > 0) return true;
    const totalDiscount = Number(cartData.total_discount || 0);
    return totalDiscount > 0;
  };

  const updateDiscountChip = (wrapper, discountCode) => {
    if (!wrapper) return;
    const chip = wrapper.querySelector('.js-cart-discount-chip');
    if (!chip) return;
    const chipCode = chip.querySelector('.js-cart-discount-code');
    const normalizedCode = String(discountCode || '').trim();
    if (!normalizedCode) {
      chip.hidden = true;
      chip.classList.add('is-hidden');
      if (chipCode) chipCode.textContent = '';
      return;
    }
    if (chipCode) chipCode.textContent = normalizedCode;
    chip.hidden = false;
    chip.classList.remove('is-hidden');
  };

  const isDiscountAppliedToCart = (cartData, code) => {
    if (!cartData || !code) return false;
    const normalizedCode = String(code).trim().toLowerCase();

    const discountCodes = Array.isArray(cartData.discount_codes) ? cartData.discount_codes : [];
    const codeMatched = discountCodes.some((entry) => {
      const isStringEntry = typeof entry === 'string';
      const entryCode = isStringEntry
        ? entry.trim().toLowerCase()
        : String(entry && entry.code ? entry.code : '')
            .trim()
            .toLowerCase();
      if (entryCode !== normalizedCode) return false;
      if (!isStringEntry && typeof entry.applicable === 'boolean') return entry.applicable;
      if (isStringEntry) return hasCartDiscountEvidence(cartData);
      return true;
    });
    if (codeMatched) return true;

    const cartDiscounts = Array.isArray(cartData.cart_level_discount_applications)
      ? cartData.cart_level_discount_applications
      : [];
    return cartDiscounts.some((entry) => {
      const title = String(entry && entry.title ? entry.title : '')
        .trim()
        .toLowerCase();
      return title === normalizedCode;
    });
  };

  const getAppliedDiscountCode = (cartData) => {
    if (!cartData) return '';
    const discountCodes = Array.isArray(cartData.discount_codes) ? cartData.discount_codes : [];
    for (const entry of discountCodes) {
      const code =
        typeof entry === 'string' ? entry.trim() : String(entry && entry.code ? entry.code : '').trim();
      if (!code) continue;
      if (typeof entry === 'object' && entry && typeof entry.applicable === 'boolean' && !entry.applicable) continue;
      if (isDiscountAppliedToCart(cartData, code)) return code;
    }
    const cartDiscounts = Array.isArray(cartData.cart_level_discount_applications)
      ? cartData.cart_level_discount_applications
      : [];
    if (hasCartDiscountEvidence(cartData) && cartDiscounts.length > 0) {
      const fallbackTitle = String(cartDiscounts[0] && cartDiscounts[0].title ? cartDiscounts[0].title : '').trim();
      if (fallbackTitle) return fallbackTitle;
    }
    return '';
  };

  const toggleDiscountPanel = (toggle) => {
    const wrapper = toggle.closest('.cart-drawer__discount');
    if (!wrapper) return;
    const panel = wrapper.querySelector('.cup-panel');
    if (!panel) return;
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    const shouldExpand = !isExpanded;
    toggle.setAttribute('aria-expanded', String(shouldExpand));
    panel.hidden = !shouldExpand;
  };

  const applyDiscountCode = async (trigger) => {
    const wrapper = trigger.closest('.cart-drawer__discount');
    if (!wrapper) return;

    const input = wrapper.querySelector('#CartDrawer-DiscountInput');
    const message = wrapper.querySelector('.cup-message');
    const removeTrigger = wrapper.querySelector('.js-cart-discount-remove');
    const setMessage = (text, state) => {
      if (!message) return;
      message.textContent = text;
      message.dataset.state = state;
    };
    const code = input ? input.value.trim() : '';
    if (!code) {
      setMessage('Enter a discount code.', 'error');
      return;
    }

    trigger.disabled = true;
    const originalAriaLabel = trigger.getAttribute('aria-label');
    const originalHtml = trigger.innerHTML;
    trigger.setAttribute('aria-label', 'Applying discount code');
    trigger.classList.add('is-loading');
    trigger.textContent = '...';
    if (message) {
      message.textContent = '';
      message.dataset.state = '';
    }

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ discount: code }),
      });

      const cartResponse = await fetch(`/cart.js?ts=${Date.now()}`, {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      const cartData = await cartResponse.json();
      const isApplied = isDiscountAppliedToCart(cartData, code);
      const appliedCode = getAppliedDiscountCode(cartData);
      if (removeTrigger) removeTrigger.hidden = !appliedCode;
      updateDiscountChip(wrapper, appliedCode);

      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
        await cartDrawerItems.onCartUpdate();
      }
      setTimeout(renderCartMergedRecommendations, 0);

      if (message) {
        if (isApplied) {
          setMessage('Discount code applied.', 'success');
        } else {
          setMessage('Discount code is invalid or not applicable.', 'error');
        }
      }
    } catch (error) {
      setMessage('Unable to apply discount. Please try again.', 'error');
    } finally {
      trigger.disabled = false;
      trigger.innerHTML = originalHtml;
      trigger.classList.remove('is-loading');
      if (input) input.value = '';
      if (originalAriaLabel !== null) {
        trigger.setAttribute('aria-label', originalAriaLabel);
      } else {
        trigger.removeAttribute('aria-label');
      }

      if (message) {
        const currentState = message.dataset.state;
        if (currentState === 'success' || currentState === 'error') {
          setTimeout(() => {
            // Only clear if state hasn't changed since we scheduled the timeout
            if (message.dataset.state === currentState) {
              message.textContent = '';
              message.dataset.state = '';
            }
          }, 5000);
        }
      }
    }
  };

  const removeDiscountCode = async (trigger) => {
    const wrapper = trigger.closest('.cart-drawer__discount');
    if (!wrapper) return;

    const input = wrapper.querySelector('#CartDrawer-DiscountInput');
    const message = wrapper.querySelector('.cup-message');
    const setMessage = (text, state) => {
      if (!message) return;
      message.textContent = text;
      message.dataset.state = state;
    };
    const clearMessageAfterDelay = (delayMs) => {
      if (!message) return;
      const currentState = message.dataset.state;
      if (!currentState) return;
      setTimeout(() => {
        if (message.dataset.state === currentState) {
          message.textContent = '';
          message.dataset.state = '';
        }
      }, delayMs);
    };

    trigger.disabled = true;
    try {
      // Optimistically hide the applied-code chip while removing the discount.
      updateDiscountChip(wrapper, '');

      await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ discount: '' }),
      });

      const cartResponse = await fetch(`/cart.js?ts=${Date.now()}`, {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      const cartData = await cartResponse.json();
      const appliedCode = getAppliedDiscountCode(cartData);
      updateDiscountChip(wrapper, appliedCode);

      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
        await cartDrawerItems.onCartUpdate();
      }
      setTimeout(renderCartMergedRecommendations, 0);

      if (input) input.value = '';
      setMessage(appliedCode ? 'Unable to remove discount. Please try again.' : 'Discount removed.', appliedCode ? 'error' : 'success');
      trigger.hidden = Boolean(appliedCode);
      if (!appliedCode) clearMessageAfterDelay(3000);
    } catch (error) {
      setMessage('Unable to remove discount. Please try again.', 'error');
    } finally {
      trigger.disabled = false;
    }
  };

  const syncDiscountChipFromCart = async () => {
    const wrapper = document.querySelector('.cart-drawer__discount');
    if (!wrapper) return;
    try {
      const cartResponse = await fetch(`/cart.js?ts=${Date.now()}`, {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      const cartData = await cartResponse.json();
      const appliedCode = getAppliedDiscountCode(cartData);
      updateDiscountChip(wrapper, appliedCode);
    } catch (error) {
      // Keep existing UI state if cart sync fails.
    }
  };

  const getActiveDrawerDiscountCode = () => {
    const wrapper = document.querySelector('.cart-drawer__discount');
    if (!wrapper) return '';
    const chip = wrapper.querySelector('.js-cart-discount-chip');
    if (!chip || chip.hidden || chip.classList.contains('is-hidden')) return '';
    const codeElement = chip.querySelector('.js-cart-discount-code');
    return String(codeElement ? codeElement.textContent : '').trim();
  };

  const redirectToCheckoutWithDiscount = (discountCode) => {
    const normalizedCode = String(discountCode || '').trim();
    if (!normalizedCode) return false;
    const discountPath = `/discount/${encodeURIComponent(normalizedCode)}`;
    const checkoutRedirect = encodeURIComponent('/checkout');
    window.location.href = `${discountPath}?redirect=${checkoutRedirect}`;
    return true;
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('.js-cart-discount-toggle');
    if (toggle) {
      toggleDiscountPanel(toggle);
      return;
    }

    const apply = event.target.closest('.js-cart-discount-apply');
    if (apply) {
      applyDiscountCode(apply);
      return;
    }

    const remove = event.target.closest('.js-cart-discount-remove');
    if (remove) {
      event.preventDefault();
      removeDiscountCode(remove);
      return;
    }
  });

  document.addEventListener('keydown', (event) => {
    const toggle = event.target.closest('.js-cart-discount-toggle');
    if (toggle && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      toggleDiscountPanel(toggle);
      return;
    }

    const input = event.target.closest('#CartDrawer-DiscountInput');
    if (input && event.key === 'Enter') {
      event.preventDefault();
      const wrapper = input.closest('.cart-drawer__discount');
      const apply = wrapper && wrapper.querySelector('.js-cart-discount-apply');
      if (apply) applyDiscountCode(apply);
    }
  });

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented) return;
    const checkoutTrigger = event.target.closest('#CartDrawer-Checkout, #CartDrawer [name="checkout"]');
    if (!checkoutTrigger) return;

    const activeCode = getActiveDrawerDiscountCode();
    if (!activeCode) return;

    event.preventDefault();
    event.stopPropagation();
    redirectToCheckoutWithDiscount(activeCode);
  });

  document.addEventListener('submit', (event) => {
    if (event.defaultPrevented) return;
    const form = event.target;
    if (!form || form.id !== 'CartDrawer-Form') return;
    const submitter = event.submitter;
    if (!submitter || !submitter.matches('#CartDrawer-Checkout, [name="checkout"]')) return;

    const activeCode = getActiveDrawerDiscountCode();
    if (!activeCode) return;

    event.preventDefault();
    redirectToCheckoutWithDiscount(activeCode);
  });

  let _lastKnownDiscountCode = '';

  const clearDiscountFromCart = () =>
    fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: '' }),
    });

  const clearDiscountUI = () => {
    const wrapper = document.querySelector('.cart-drawer__discount');
    updateDiscountChip(wrapper, '');
    const input = wrapper && wrapper.querySelector('#CartDrawer-DiscountInput');
    if (input) input.value = '';
  };

  subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData } = {}) => {
    if (!cartData) return;

    // Track the applied code while the cart has items — before the chip gets hidden by re-render
    if (cartData.item_count > 0) {
      const code = getAppliedDiscountCode(cartData);
      if (code) _lastKnownDiscountCode = code;
    }

    if (cartData.item_count === 0) {
      // Prefer the tracked code; fall back to any raw entry in discount_codes
      // (Shopify keeps the code there with applicable:false on an empty cart)
      let code = _lastKnownDiscountCode;
      if (!code) {
        const rawCodes = Array.isArray(cartData.discount_codes) ? cartData.discount_codes : [];
        code = rawCodes.reduce((found, entry) => {
          if (found) return found;
          return (typeof entry === 'string' ? entry : String(entry?.code || '')).trim();
        }, '');
      }
      if (!code) return;

      window.__cartDiscountShouldBeCleared = true;
      _lastKnownDiscountCode = '';
      clearDiscountUI();
      clearDiscountFromCart();
      return;
    }

    if (window.__cartDiscountShouldBeCleared) {
      window.__cartDiscountShouldBeCleared = false;
      clearDiscountFromCart().then(() => {
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
          cartDrawerItems.onCartUpdate();
        } else {
          clearDiscountUI();
        }
      });
    }
  });

  syncDiscountChipFromCart();
}

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

function cartRecoPrice(moneyInCents) {
  if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
    return Shopify.formatMoney(moneyInCents);
  }
  return `$${(Number(moneyInCents || 0) / 100).toFixed(2)}`;
}

function escapeRecoHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

async function fetchRelatedRecommendationsByProductId(productId, limit = 6) {
  const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`);
  const data = await response.json();
  return Array.isArray(data.products) ? data.products : [];
}

function getFirstAvailableVariantId(product) {
  if (!product || !Array.isArray(product.variants)) return null;
  const availableVariant = product.variants.find((variant) => variant.available);
  return availableVariant ? availableVariant.id : null;
}

function bindRecoSlider(container) {
  const track = container.querySelector('.js-cart-reco-track');
  const prevButton = container.querySelector('.js-cart-reco-prev');
  const nextButton = container.querySelector('.js-cart-reco-next');
  if (!track || !prevButton || !nextButton) return;

  const originalSlides = Array.from(track.querySelectorAll('.cc'));
  if (!originalSlides.length) return;

  const makeClone = (slide) => {
    const clone = slide.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    return clone;
  };

  const prependFragment = document.createDocumentFragment();
  originalSlides.forEach((slide) => prependFragment.appendChild(makeClone(slide)));
  track.prepend(prependFragment);

  const appendFragment = document.createDocumentFragment();
  originalSlides.forEach((slide) => appendFragment.appendChild(makeClone(slide)));
  track.append(appendFragment);

  const getSectionWidth = () => track.scrollWidth / 3;
  const setInitialPosition = () => {
    track.scrollLeft = getSectionWidth();
  };

  const normalizeInfiniteScroll = () => {
    const sectionWidth = getSectionWidth();
    if (!sectionWidth) return;

    if (track.scrollLeft < sectionWidth * 0.5) {
      track.scrollLeft += sectionWidth;
    } else if (track.scrollLeft > sectionWidth * 1.5) {
      track.scrollLeft -= sectionWidth;
    }
  };

  setInitialPosition();

  const scrollByCard = (direction) => {
    const firstCard = track.querySelector('.cc');
    const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : track.clientWidth;
    track.scrollBy({ left: direction * cardWidth, behavior: 'smooth' });
  };

  prevButton.addEventListener('click', () => scrollByCard(-1));
  nextButton.addEventListener('click', () => scrollByCard(1));
  track.addEventListener('scroll', normalizeInfiniteScroll, { passive: true });
  window.addEventListener('resize', setInitialPosition);
}

async function renderCartMergedRecommendations() {
  const container = document.querySelector('.js-cart-recommendations');
  if (!container) return;

  const cartProductIds = String(container.dataset.cartProductIds || '')
    .split(',')
    .map((id) => Number(id.trim()))
    .filter((id) => Number.isFinite(id) && id > 0);
  const limit = Number(container.dataset.limit || 12);
  const headingText = String(container.dataset.heading || '☕ Pairs perfectly with');
  if (!cartProductIds.length) return;

  try {
    const productFetchResults = await Promise.allSettled(
      cartProductIds.map((productId) => fetchRelatedRecommendationsByProductId(productId, Math.max(limit * 3, 6)))
    );

    const inCartProductIds = new Set(cartProductIds);
    const mergedProducts = new Map();

    productFetchResults.forEach((result) => {
      if (result.status !== 'fulfilled') return;
      result.value.forEach((product, index) => {
        if (!product || !product.id || inCartProductIds.has(product.id)) return;
        const existing = mergedProducts.get(product.id);
        if (existing) {
          existing.score += 1;
          existing.rankSum += index + 1;
          return;
        }
        mergedProducts.set(product.id, {
          product,
          score: 1,
          rankSum: index + 1,
        });
      });
    });

    const products = Array.from(mergedProducts.values())
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.rankSum - b.rankSum;
      })
      .slice(0, limit)
      .map((entry) => entry.product);

    if (!products.length) {
      container.innerHTML = '';
      return;
    }

    const cards = products
      .map((product) => {
        const variantId = getFirstAvailableVariantId(product) || product.id;
        const itemPrice = product.price ? (product.price / 100).toFixed(2) : '0';
        const image = product.featured_image
          ? `<img src="${escapeRecoHtml(product.featured_image)}" alt="${escapeRecoHtml(product.title)}" loading="lazy" width="44" height="50">`
          : '';
        const rating = product.rating && product.rating_count ? ` · ★ ${product.rating} (${product.rating_count})` : '';
        const addButton = `<a class="ca" href="${escapeRecoHtml(product.url)}" aria-label="View ${escapeRecoHtml(product.title)}"><svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path d="M12 5c5.5 0 9.5 5.2 10.8 7-.1.2-.3.4-.4.6C20.9 14.8 17 19 12 19S3.1 14.8 1.6 12.6c-.1-.2-.3-.4-.4-.6C2.5 10.2 6.5 5 12 5Zm0 2C8.1 7 5 10.3 3.4 12c1.6 1.7 4.7 5 8.6 5s7-3.3 8.6-5C19 10.3 15.9 7 12 7Zm0 1.8a3.2 3.2 0 1 1 0 6.4 3.2 3.2 0 0 1 0-6.4Zm0 2a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4Z" fill="currentColor"/></svg></a>`;

        return `
          <div class="cc"
            data-item-id="${variantId}"
            data-item-name="${escapeRecoHtml(product.title)}"
            data-item-brand="${escapeRecoHtml(product.vendor || '')}"
            data-item-category="${escapeRecoHtml(product.type || '')}"
            data-item-price="${itemPrice}"
            data-item-list-id="cart_recommendations"
            data-item-list-name="Cart Recommendations"
          >
            <a class="ci" href="${escapeRecoHtml(product.url)}" aria-label="${escapeRecoHtml(product.title)}">
              ${image}
            </a>
            <div class="cf">
              <div class="cn">${escapeRecoHtml(product.title)}</div>
              <div class="cp">${cartRecoPrice(product.price)}${rating}</div>
            </div>
            ${addButton}
          </div>
        `;
      })
      .join('');

    container.innerHTML = `
      <div class="cs-header">
        <div class="ct">${escapeRecoHtml(headingText)}</div>
        <div class="cs-nav">
          <button type="button" class="ca ca--nav js-cart-reco-prev" aria-label="Previous recommendations">‹</button>
          <button type="button" class="ca ca--nav js-cart-reco-next" aria-label="Next recommendations">›</button>
        </div>
      </div>
      <div class="cs-track js-cart-reco-track">${cards}</div>
    `;
    bindRecoSlider(container);
  } catch (error) {
    container.innerHTML = '';
  }
}

document.addEventListener('DOMContentLoaded', renderCartMergedRecommendations);
subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
  setTimeout(renderCartMergedRecommendations, 0);
});
